<template>
  <div>
    <div
      v-if="isLoading"
      class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
    >
      <div
        class="animate-spin rounded-full h-32 w-32 border-t-4 border-b-4 border-primary-purple"
      ></div>
    </div>
    <div v-if="errorMessage" class="flex justify-center mb-4">
      <div
        role="alert"
        class="flex gap-2 w-1/2 justify-center p-2.5 mt-6 text-sm leading-5 text-center text-red-900 bg-red-50 rounded-lg max-md:flex-wrap"
        style="max-height: 80px; overflow-y: auto"
      >
        <div
          class="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full"
        >
          <img
            src="../../assets/shared/failure.svg"
            alt="Error Icon"
            class="w-4 h-4"
          />
        </div>
        <p>Something went wrong. Please try again later.</p>
      </div>
    </div>
    <div
      class="flex flex-col w-3/5 gap-0 mx-auto justify-center mt-8 max-md:mt-4 max-md:w-full border border-gray-300 rounded-xl bg-white overflow-hidden"
    >
      <div
        class="flex flex-row items-center justify-between px-8 py-6 cursor-pointer transition-all duration-200 bg-white rounded-t-xl"
        @click="isSubscriptionExpanded = !isSubscriptionExpanded"
      >
        <div class="flex flex-col items-start">
          <span class="text-xl font-bold">{{ subscriptionPlanName }}</span>
          <span class="text-2xl font-bold mt-1">
            ${{ billingAmount() }}
            <span class="text-gray-600 text-base font-normal"
              >/{{ subscriptionPlanBillingCycle }}</span
            >
          </span>
        </div>
        <div
          v-html="CHEVRON_RIGHT_ICON"
          :class="[
            'w-8 h-8 text-primary-purple transition-transform duration-200',
            isSubscriptionExpanded ? 'rotate-90' : 'rotate-0',
          ]"
        ></div>
      </div>
      <div
        v-if="isSubscriptionExpanded"
        class="px-8 pb-8 bg-white rounded-b-xl"
      >
        <div class="border-t border-gray-200 my-4"></div>
        <div class="pt-2">
          <div class="flex flex-col mb-4">
            <h3 class="text-lg font-semibold">What's included:</h3>
          </div>
          <ul class="space-y-3 mb-6 w-full items-start text-left px-2">
            <li
              v-for="feature in subscriptionPlanFeatures"
              :key="feature"
              class="flex items-start text-left"
            >
              <span class="text-green-500 mr-2">✓</span>
              <span class="text-gray-600">{{ feature }}</span>
            </li>
          </ul>
        </div>
        <div class="border-t border-gray-200 my-6"></div>
        <div class="mb-6">
          <div class="flex flex-col mb-4">
            <h3 class="text-lg font-semibold">Subscription Details</h3>
          </div>
          <div class="flex flex-row items-center gap-2 mb-2">
            <div
              v-html="CALENDAR_ICON"
              class="w-6 h-6 text-primary-purple flex items-center justify-center"
            ></div>
            <span class="text-sm font-normal leading-5">
              <span>
                Starts
                <span class="font-semibold">{{ subscriptionStartDate }}</span>
              </span>
            </span>
          </div>
          <div class="flex flex-row items-center gap-2 mb-2">
            <div
              v-html="CLOCK_ICON"
              class="w-6 h-6 text-primary-purple flex items-center justify-center"
            ></div>
            <span class="text-sm font-normal leading-5">
              <span class="font-semibold">${{ billingAmount() }}</span>
              Due Now (Net30)
            </span>
          </div>
          <div class="flex flex-row items-center gap-2 mb-2">
            <div
              v-html="BILL_ICON"
              class="w-6 h-6 text-primary-purple flex items-center justify-center"
            ></div>
            <span class="text-sm font-normal leading-5">
              Billed
              <span class="font-semibold">
                {{
                  subscriptionPlanBillingCycle.charAt(0).toUpperCase() +
                  subscriptionPlanBillingCycle.slice(1)
                }}
              </span>
            </span>
          </div>
          <div
            class="flex flex-row items-center gap-2"
            v-if="subscriptionAutoRenewal == true"
          >
            <div
              v-html="RENEWAL_ICON"
              class="w-6 h-6 text-primary-purple flex items-center justify-center"
            ></div>
            <span class="text-sm font-normal leading-5">
              Renewal
              <span class="font-semibold"> Auto </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { gql } from "@apollo/client/core";
import { useQuery } from "@vue/apollo-composable";
import {
  CLOCK_ICON,
  BILL_ICON,
  RENEWAL_ICON,
  CALENDAR_ICON,
  CHEVRON_RIGHT_ICON,
} from "@/assets/svg/shared/svg-constants";

const FETCH_SUBSCRIPTION_BASED_COMPANY_INVITE = gql`
  query fetchSubscriptionBasedCompanyInvite {
    fetchSubscriptionBasedCompanyInvite {
      id
      subscriptionPlanTierId
      subscriptionName
      features
      billingCycle
      billingAmount
      couponId
      couponValue
      couponType
      startDate
      autoRenewal
    }
  }
`;

export default defineComponent({
  name: "AccountBalanceSubscriptionReview",
  emits: ["subscription-data"],
  setup(props, { emit }) {
    const isLoading = ref(true);
    const subscriptionPlanTierId = ref("");
    const subscriptionBasedCompanyInviteId = ref("");
    const subscriptionPlanName = ref("");
    const subscriptionPlanFeatures = ref([]);
    const subscriptionPlanBillingCycle = ref("");
    const subscriptionPlanBillingAmount = ref(0);
    const subscriptionPlanCouponId = ref("");
    const subscriptionPlanCouponValue = ref(0);
    const subscriptionPlanCouponType = ref("");
    const subscriptionStartDate = ref("");
    const subscriptionAutoRenewal = ref(false);
    const isSubscriptionExpanded = ref(false);
    const errorMessage = ref("");

    const { onResult, onError } = useQuery(
      FETCH_SUBSCRIPTION_BASED_COMPANY_INVITE,
      {
        fetchPolicy: "network-only",
      }
    );

    onMounted(() => {
      fetchSubscriptionDetails();
    });

    const fetchSubscriptionDetails = () => {
      onResult((result) => {
        isLoading.value = false;
        if (result) {
          if ((result?.errors?.length ?? 0) > 0) {
            errorMessage.value = "Error fetching subscription details.";
          } else if (result.data?.fetchSubscriptionBasedCompanyInvite) {
            subscriptionPlanTierId.value =
              result.data.fetchSubscriptionBasedCompanyInvite.subscriptionPlanTierId;
            subscriptionBasedCompanyInviteId.value =
              result.data.fetchSubscriptionBasedCompanyInvite.id;
            subscriptionPlanName.value =
              result.data.fetchSubscriptionBasedCompanyInvite.subscriptionName;
            subscriptionPlanFeatures.value =
              result.data.fetchSubscriptionBasedCompanyInvite.features;
            subscriptionPlanBillingCycle.value =
              result.data.fetchSubscriptionBasedCompanyInvite.billingCycle;
            subscriptionPlanBillingAmount.value =
              result.data.fetchSubscriptionBasedCompanyInvite.billingAmount;
            subscriptionPlanCouponId.value =
              result.data.fetchSubscriptionBasedCompanyInvite.couponId;
            subscriptionPlanCouponValue.value =
              result.data.fetchSubscriptionBasedCompanyInvite.couponValue;
            subscriptionPlanCouponType.value =
              result.data.fetchSubscriptionBasedCompanyInvite.couponType;
            subscriptionStartDate.value = convertToDate(
              result.data.fetchSubscriptionBasedCompanyInvite.startDate
            );
            subscriptionAutoRenewal.value =
              result.data.fetchSubscriptionBasedCompanyInvite.autoRenewal;

            emit("subscription-data", {
              subscriptionPlanTierId: subscriptionPlanTierId.value,
              subscriptionBasedCompanyInviteId:
                subscriptionBasedCompanyInviteId.value,
              couponId: subscriptionPlanCouponId.value,
            });
            errorMessage.value = "";
          }
        }
      });
      onError((error) => {
        isLoading.value = false;
        if (error) {
          errorMessage.value = "Error fetching subscription details.";
        }
      });
    };

    const convertToDate = (date: string) => {
      if (date) {
        return new Date(date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      }
      return "";
    };

    const billingAmount = () => {
      if (subscriptionPlanCouponId.value) {
        if (subscriptionPlanCouponType.value === "percentage") {
          return (
            subscriptionPlanBillingAmount.value *
            (Math.round((1 - subscriptionPlanCouponValue.value / 100) * 100) /
              100)
          );
        } else {
          return (
            subscriptionPlanBillingAmount.value -
            subscriptionPlanCouponValue.value
          );
        }
      } else {
        return subscriptionPlanBillingAmount.value;
      }
    };

    return {
      isLoading,
      subscriptionPlanTierId,
      subscriptionBasedCompanyInviteId,
      subscriptionPlanName,
      subscriptionPlanFeatures,
      subscriptionPlanBillingCycle,
      subscriptionPlanBillingAmount,
      subscriptionPlanCouponId,
      subscriptionPlanCouponValue,
      subscriptionPlanCouponType,
      subscriptionStartDate,
      subscriptionAutoRenewal,
      isSubscriptionExpanded,
      billingAmount,
      CLOCK_ICON,
      BILL_ICON,
      RENEWAL_ICON,
      CALENDAR_ICON,
      CHEVRON_RIGHT_ICON,
      errorMessage,
    };
  },
});
</script>
